import React, {Component} from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {cloneDeep} from 'lodash';
import {
    setManagedUser, setSelectedEditorCountryOptions, setEditorCountriesChanged, fetchEditorRightsManagement,
    saveEditorRightsManagement, clearEditorRightsManagement, fetchDealerRightsManagement, clearDealerRightsManagement,
    setInitialEditorCountryOptions, clearExternalEditorRightsManagement, fetchExternalEditorRightsManagement,
} from '../../../actions/admin/actionAdminRightsManagement';
import {clearAlerts} from '../../../actions/alertsActions';
import {handleUserSearch} from '../../../utils/userUtils';
import {countryGroups} from '../../../constants/Utils';
import {Panel} from '../../common/StyledComponents';
import checkRoles from '../../common/roleChecker/RoleChecker';

import SelectUser from './SelectUser';
import SelectEditorCountries from './SelectEditorCountries';
import Loader from '../../Loader';
import PageHeader from '../../common/PageHeader';
import formTypeMessages from '../../../intl/modules/formTypeMessages';
import errorMessages from '../../../intl/common/errorMessages';
import {handleFetchUsersError} from '../../../actions/actionRole';

class EditorRightsManagementPage extends Component {
    componentDidMount() {
        if (this.props.managedUser && this.props.managedUser.ipn) {
            this.props.setEditorCountriesChanged(false);
            this.loadData(this.props.managedUser);
        }
    }

    handleUserSearch = (inputValue, callback) => {
        handleUserSearch(inputValue, callback, this.props.handleFetchUsersError);
    };

    handleUserSelection = user => {
        this.props.setManagedUser(user);
        this.loadData(user);
    };

    handleCountrySelectionChange = newOptions => {
        if (newOptions === null) {
            this.setSelectedOptionsChanged([]);
            this.props.setSelectedEditorCountryOptions([]);
        } else {
            this.setSelectedOptionsChanged(newOptions);
            this.props.setSelectedEditorCountryOptions(newOptions);
        }
    };

    handleAllCountriesClick = () => {
        const selectedCountries = {};

        Object.keys(this.props.editorData || {}).forEach(
            countryIso => selectedCountries[countryIso] = true
        );

        const selectedOptions = this.setEditorDataAsCountryOptions(selectedCountries);
        this.setSelectedOptionsChanged(selectedOptions);
    };

    handleSaveClick = () => {
        const selectedCountries = {};
        Object.keys(countryGroups).forEach(
            groupKey => selectedCountries[countryGroups[groupKey]] = false
        );

        if (this.props.selectedOptions) {
            this.props.selectedOptions.forEach(
                ({value}) => {
                    selectedCountries[value] = true;
                }
            )
        }

        this.props.saveEditorRightsManagement(this.props.domain, this.props.managedUser, selectedCountries);
    };

    handleCancelClick = () => {
        this.props.setSelectedEditorCountryOptions(cloneDeep(this.props.initialOptions));
        this.props.setEditorCountriesChanged(false);
    };

    loadData = user => {
        this.props.clearAlerts();
        this.props.clearEditorRightsManagement();
        this.props.clearExternalEditorRightsManagement();
        this.props.clearDealerRightsManagement();
        this.props.fetchDealerRightsManagement(this.props.domain, user);
        this.props.fetchExternalEditorRightsManagement(this.props.domain, user);
        this.props.fetchEditorRightsManagement(this.props.domain, user);
    };

    setEditorDataAsCountryOptions = editorData => {
        const result = Object.keys(editorData || {}).map(countryIso => ({value: countryIso}));
        this.props.setSelectedEditorCountryOptions(cloneDeep(result));

        return result;
    };

    setSelectedOptionsChanged = newOptions => this.props.setEditorCountriesChanged(
        JSON.stringify(
            cloneDeep(this.props.initialOptions)
                .sort((option1, option2) => option1.value.localeCompare(option2.value))
        ) !== JSON.stringify(
        newOptions
            .sort((option1, option2) => option1.value.localeCompare(option2.value))
        )
    );

    render() {
        const {isLoading, managedUser, editorData, isDealer, isExternalEditor, domain} = this.props;
        if (isLoading) {
            return <Loader/>;
        }
        return (
            <div>
                <PageHeader title={
                    <FormattedMessage id="admin.title.auth.mng.editor"
                                      defaultMessage="Authorization Management - {domain} - Editor"
                                      values={{domain: <FormattedMessage {...formTypeMessages[domain]}/>}}/>
                }/>
                <div className="mb-sm-4">
                    <SelectUser handleUserSelection={this.handleUserSelection}
                                handleUserSearch={this.handleUserSearch}/>
                </div>
                {isDealer &&
                <Panel className="alert alert-warning">
                    <FormattedMessage {...errorMessages.DEALER_ROLE_SET}/>
                </Panel>
                }
                {isExternalEditor &&
                <Panel className="alert alert-warning">
                    <FormattedMessage {...errorMessages.EXTERNAL_EDITOR_ROLE_SET}/>
                </Panel>
                }
                {Object.keys(editorData).length > 0 && managedUser && managedUser.ipn && !isDealer && !isExternalEditor &&
                <SelectEditorCountries handleCountrySelectionChange={this.handleCountrySelectionChange}
                                       handleAllCountriesClick={this.handleAllCountriesClick}
                                       handleSaveClick={this.handleSaveClick}
                                       handleCancelClick={this.handleCancelClick}/>
                }
            </div>
        );
    }
}

EditorRightsManagementPage.propTypes = {
    managedUser: PropTypes.object.isRequired,
    setManagedUser: PropTypes.func.isRequired,
    handleFetchUsersError: PropTypes.func.isRequired,
    setSelectedEditorCountryOptions: PropTypes.func.isRequired,
    editorData: PropTypes.object.isRequired,
    selectedOptions: PropTypes.array,
    initialOptions: PropTypes.array.isRequired,
    setEditorCountriesChanged: PropTypes.func.isRequired,
    clearAlerts: PropTypes.func.isRequired,
    clearEditorRightsManagement: PropTypes.func.isRequired,
    fetchEditorRightsManagement: PropTypes.func.isRequired,
    clearDealerRightsManagement: PropTypes.func.isRequired,
    fetchDealerRightsManagement: PropTypes.func.isRequired,
    saveEditorRightsManagement: PropTypes.func.isRequired,
    setInitialEditorCountryOptions: PropTypes.func.isRequired,
    loggedUser: PropTypes.object.isRequired,
    intl: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isExternalEditor: PropTypes.bool.isRequired,
    isDealer: PropTypes.bool.isRequired,
    domain: PropTypes.string.isRequired
};

const mapStateToProps = state => ({
    isLoading: state.adminRightsManagement.isLoading,
    managedUser: state.adminRightsManagement.managedUser,
    editorData: state.adminRightsManagement.editorData,
    isExternalEditor: Object.values(state.adminRightsManagement.externalEditorData || {}).some(value => value),
    isDealer: Object.values(state.adminRightsManagement.dealerData || {}).some(value => value),
    loggedUser: state.profile.userDetail,
    initialOptions: state.adminRightsManagement.initialEditorOptions,
    selectedOptions: state.adminRightsManagement.selectedEditorOptions,
});

export default checkRoles(connect(mapStateToProps, {
    setManagedUser,
    clearAlerts,
    clearEditorRightsManagement,
    fetchEditorRightsManagement,
    clearExternalEditorRightsManagement,
    fetchExternalEditorRightsManagement,
    clearDealerRightsManagement,
    fetchDealerRightsManagement,
    setSelectedEditorCountryOptions,
    setEditorCountriesChanged,
    saveEditorRightsManagement,
    setInitialEditorCountryOptions,
    handleFetchUsersError,
})(injectIntl(EditorRightsManagementPage)), ['DB_ADMIN', 'CC_ADMIN', 'MA_ADMIN']);
